import React, { ComponentProps } from "react";

import {
  faCircleNotch,
  faInfoCircle,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { Breakdown } from "src/base-components/Breakdown";
import { Pill, PillVariants } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { Callout } from "src/design-system/Callout";
import { Tooltip } from "src/design-system/Tooltip";
import { isJobRunRunning } from "src/flowContainer/RunFlowButton/utils";
import { useOutputNodeJobRunCounts } from "src/flowContainer/TestRunsSidePane/hooks";
import { JobRun, JobRunStatusType } from "src/jobs/types";
import { MAX_LTR_ROW_COUNT } from "src/nodeEditor/JobRunIntermediateResultTableWrapper/hooks";
import { useCurrentJobRun } from "src/store/runState/RunState";
import { formatNumber } from "src/utils/numbers";
import { assertUnreachable } from "src/utils/typeUtils";

const buildBarsData = ({
  success_rows_count,
  failed_rows_count,
  total_rows_count,
  ignored_rows_count,
}: {
  success_rows_count: number;
  failed_rows_count: number;
  total_rows_count: number;
  ignored_rows_count: number;
}) => {
  const processedCount =
    success_rows_count + failed_rows_count + ignored_rows_count;

  // Satisfies allows us to complain with <Breakdown/> while
  // having the option of specifying additional fields
  // and don't get a TypeScript error
  const data: (ComponentProps<typeof Breakdown>["bars"][number] & {
    color: PillVariants;
    type: string;
    displayText: string;
  })[] = [];

  data.push({
    type: "success",
    displayText: "Successful",
    count: success_rows_count,
    color: "green" as const,
  });

  data.push({
    type: "failed",
    displayText: "Failed",
    count: failed_rows_count,
    color: "red",
  });

  data.push({
    type: "ignored",
    displayText: "Ignored",
    count: ignored_rows_count,
    color: "gray",
  });

  data.push({
    type: "not_started",
    displayText: "Waiting",
    count: total_rows_count - processedCount,
    color: "gray",
  });

  return data;
};

type Props = {
  jobRun: JobRun;
  footer: string;
};

export const JobRunStatus: React.FC<Props> = ({ jobRun, footer }) => {
  const outputCounts = useOutputNodeJobRunCounts(jobRun.id);
  const currentJobRun = useCurrentJobRun();

  const metrics =
    currentJobRun?.id === jobRun.id
      ? {
          success_rows_count: outputCounts?.success ?? 0,
          failed_rows_count: outputCounts?.failed ?? 0,
          ignored_rows_count: outputCounts?.ignored ?? 0,
          // Fallback to MAX_LTR_ROW_COUNT to avoid the breakdown from not rendering when the job run has no total rows count.
          // This happens when the job run is still initializing.
          total_rows_count: jobRun.total_rows_count || MAX_LTR_ROW_COUNT,
        }
      : {
          success_rows_count: jobRun.success_rows_count,
          failed_rows_count: jobRun.failed_rows_count,
          ignored_rows_count: jobRun.ignored_rows_count,
          total_rows_count: jobRun.total_rows_count,
        };

  const data = buildBarsData({
    success_rows_count: metrics.success_rows_count,
    failed_rows_count: metrics.failed_rows_count,
    total_rows_count: metrics.total_rows_count,
    ignored_rows_count: metrics.ignored_rows_count,
  });

  const isWaitingForResults = (() => {
    if (currentJobRun?.id === jobRun.id) {
      return (outputCounts?.total ?? 0) < jobRun.total_rows_count;
    }

    return false;
  })();

  const button = (() => {
    switch (jobRun.status.type) {
      case JobRunStatusType.Writing:
      case JobRunStatusType.Completed:
      case JobRunStatusType.Running:
        return (
          <div
            className="flex h-4 w-full cursor-pointer items-center justify-center gap-x-1.5"
            data-loc="run-test-cases-by-status-button"
          >
            <Breakdown bars={data} />
          </div>
        );

      case JobRunStatusType.Failed:
        return (
          <Tooltip
            title={jobRun.status.error_title}
            body={jobRun.status.error_details}
          >
            <Pill variant="red">
              <Pill.Icon icon={faTriangleExclamation} />
              <Pill.Text>Test run failed</Pill.Text>
            </Pill>
          </Tooltip>
        );

      case JobRunStatusType.Stopped:
        return (
          <Pill variant="red">
            <Pill.Icon icon={faTriangleExclamation} />
            <Pill.Text>Test run stopped</Pill.Text>
          </Pill>
        );

      default:
        assertUnreachable(jobRun.status);
        return null;
    }
  })();

  if (
    [JobRunStatusType.Failed, JobRunStatusType.Stopped].includes(
      jobRun.status.type,
    )
  ) {
    return button;
  }

  return (
    <CustomPopover
      button={button}
      offsetX={24}
      placement="right"
      wrapperClassName="w-full"
    >
      <div className="relative flex w-122 cursor-default flex-col gap-y-3 px-5 py-2">
        {jobRun.total_rows_count === 0 && (
          <Callout type="neutral" icon={faCircleNotch} iconSpin>
            Initializing...
          </Callout>
        )}
        {!isJobRunRunning(jobRun) && isWaitingForResults && (
          <Callout type="neutral" icon={faCircleNotch} iconSpin>
            Collating results...
          </Callout>
        )}
        <div className="border border-gray-100 text-left text-gray-800">
          <div className="flex h-8 bg-gray-50 font-inter-medium-12px">
            <div className="flex min-w-[127px] items-center border-gray-100 border-r px-2">
              Status
            </div>
            <div className="flex min-w-0 flex-1 items-center border-gray-100 border-r px-2">
              Decisions
            </div>
            <div className="flex min-w-[62px] items-center border-gray-100 border-r px-2"></div>
            <div className="flex min-w-[169px] items-center px-2"></div>
          </div>
          {data.map((entry) => (
            <div
              key={entry.type}
              className="flex h-8 border-gray-100 border-t font-inter-normal-12px"
            >
              <div className="flex min-w-[127px] items-center border-gray-100 border-r px-1.5">
                <Pill
                  variant={
                    entry.color === "indigo" ? "indigo-light" : entry.color
                  }
                >
                  <Pill.Text>{entry.displayText}</Pill.Text>
                </Pill>
              </div>
              <div className="flex min-w-0 flex-1 items-center border-gray-100 border-r px-2">
                <p
                  className="flex-1 text-right font-inter-normal-12px text-gray-800"
                  data-loc={`run-decisions-by-status-button-${entry.type}-count`}
                >
                  {formatNumber(entry.count)}
                </p>
              </div>
              <div className="flex min-w-[62px] items-center border-gray-100 border-r px-2">
                <p
                  className="flex-1 text-right font-inter-normal-12px text-gray-500"
                  data-loc={`run-decisions-by-status-button-${entry.type}-percentage`}
                >
                  (
                  {formatNumber(
                    metrics.total_rows_count !== 0
                      ? entry.count / metrics.total_rows_count
                      : 0,
                    {
                      style: "percent",
                    },
                  )}
                  )
                </p>
              </div>
              <div className="flex min-w-[169px] items-center px-2">
                <Breakdown bars={data} highlightOnly={entry.color} />
              </div>
            </div>
          ))}
          <div className="flex h-8 border-gray-100 border-t font-inter-normal-12px">
            <div className="flex min-w-[127px] items-center border-gray-100 border-r px-1.5 font-inter-normal-12px text-gray-800">
              Total
            </div>
            <div className="flex min-w-0 flex-1 items-center border-gray-100 border-r px-2">
              <p className="flex-1 text-right font-inter-semibold-13px text-gray-800">
                {formatNumber(metrics.total_rows_count)}
              </p>
            </div>
            <div className="flex min-w-[62px] items-center border-gray-100 border-r px-2">
              <p className="flex-1 text-right font-inter-normal-12px text-gray-500">
                (100%)
              </p>
            </div>
            <div className="flex min-w-[169px] items-center px-2">
              <Breakdown bars={data} />
            </div>
          </div>
        </div>
        {(isJobRunRunning(jobRun) || isWaitingForResults) && (
          <Callout type="neutral" icon={faInfoCircle}>
            Numbers displayed above are estimates. Detailed results will be
            available once the test run is complete.
          </Callout>
        )}
        <p className="font-inter-normal-12px text-gray-600">{footer}</p>
      </div>
    </CustomPopover>
  );
};
